import { Skeleton, readerTimezone } from '@h2hspark/ui';
import { MessageSquare } from 'lucide-react';
import * as React from 'react';

import { useMarkThreadRead, useThread } from './api';
import {
  NO_THREAD_YET,
  OLDER_MESSAGES_LABEL,
  closedNoticeFor,
  initialsOf,
  messageStamp,
  personName,
  roleAtCompany,
  threadSubtitle,
} from './constants';
import { MessageComposer } from './message-composer-form';
import { MessageBubbles } from './thread-bubbles-panel';
import type { Thread } from './types';

/**
 * The thread itself — header, bubbles, composer — without deciding where it sits.
 *
 * `Web App.dc.html` §2.16's overlay draws it in a 460px card; §2.11's connection-detail card
 * draws the same conversation inside the left column. **One component**, for the reason
 * `thread-bubbles-panel.tsx` gives: two renderings of one conversation drift, and the one that drifts
 * is the one nobody is looking at. `thread-dialog.tsx` and `messages-panel.tsx` are placements.
 *
 * ## Opening marks it read, once (`MEM-055` AC-3)
 *
 * On the reference, not on every render and not on every refetch — a member scrolling a thread
 * that polls would otherwise post a mark-read per poll. It runs after the thread resolves rather
 * than on mount, so a 404 marks nothing, and it is deliberately not awaited: a failure leaves a
 * dot on a thread they have read, and an error toast over a conversation they are trying to read
 * would be worse.
 *
 * ## Read-only is a line and an absence, not a disabled control
 *
 * `MEM-054` AC-5 and AC-7. Where the thread is closed the composer is **not rendered** and a
 * sentence says why — `constants.ts` has both, and both are Sumit's rulings of 2026-09-11 rather
 * than copy. A disabled textarea would be a control a member keeps trying, with no explanation
 * attached to it.
 */
export function ThreadPanel({
  connectionRef,
  variant,
  headerless = false,
}: {
  readonly connectionRef: string;
  readonly variant: 'overlay' | 'card';
  /** `true` where the surface already draws the person — the connection detail's page header. */
  readonly headerless?: boolean;
}): React.ReactElement {
  const query = useThread(connectionRef, { enabled: true });
  const markRead = useMarkThreadRead();
  const reader = React.useMemo(() => readerTimezone(), []);
  const stampFor = React.useCallback(
    (instant: string) => messageStamp(instant, reader),
    [reader],
  );

  const thread = query.data?.thread;
  const resolved = thread !== undefined;
  React.useEffect(() => {
    if (!resolved) return;
    markRead.mutate({ connectionRef });
    // The reference, and nothing else. `markRead` is a stable mutation object from React Query
    // and re-running on it would fire once per refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionRef, resolved]);

  /**
   * **A thread opens at its end**, which is where a conversation is.
   *
   * The scrollback is bounded — §2.16 gives it 320px on the desktop — so without this a member
   * opening a thread with any history lands on its *oldest* message and has to scroll to find the
   * one they were told about. Every messenger opens at the bottom; this is that, done on the
   * message count so it also runs after a send appends one.
   *
   * `behavior: 'auto'`, not `'smooth'`: an animated scroll on open is a screen that moves while
   * somebody is reading it, and on a long thread it is a visible rewind.
   */
  const scrollback = React.useRef<HTMLDivElement | null>(null);
  const messageCount = thread?.messages.length ?? 0;
  React.useEffect(() => {
    const element = scrollback.current;
    if (element === null) return;
    element.scrollTop = element.scrollHeight;
  }, [messageCount, connectionRef]);

  if (query.isPending) {
    return (
      <div className="space-y-3 p-[22px]">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-16 w-3/4" />
        <Skeleton className="ml-auto h-16 w-2/3" />
      </div>
    );
  }
  if (thread === undefined) {
    return (
      <p className="p-[22px] text-sm text-muted-foreground">
        That conversation is not one of yours.
      </p>
    );
  }

  const closed = closedNoticeFor(thread.closedReason);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {headerless ? null : <ThreadHeader thread={thread} />}

      {/* §2.16's scrollback and §2.11's body, both on `--surface-page-alt`. */}
      <div
        ref={scrollback}
        className={
          variant === 'overlay'
            ? 'min-h-0 flex-1 overflow-y-auto bg-ivory-100 px-[22px] py-5 md:max-h-[320px]'
            : 'min-h-0 flex-1 overflow-y-auto bg-ivory-100 px-[22px] py-5'
        }
      >
        {thread.messages.length === 0 ? (
          <div className="flex flex-col items-center gap-2.5 px-[22px] py-[34px] text-center">
            <MessageSquare className="h-7 w-7 text-border-strong" aria-hidden />
            <p className="text-sm text-muted-foreground">{NO_THREAD_YET}</p>
          </div>
        ) : (
          <>
            {thread.olderCursor === null ? null : (
              // Drawn rather than paged automatically: a thread that loaded its history on scroll
              // would move the message somebody is reading. Wired in a later change — the control
              // is here so the absence is a visible `disabled`, not a missing affordance.
              <p className="mb-3 text-center text-2xs text-text-subtle">{OLDER_MESSAGES_LABEL}</p>
            )}
            <MessageBubbles
              messages={thread.messages}
              stampFor={stampFor}
              variant={variant}
            />
          </>
        )}
      </div>

      {closed === null ? (
        <div className={variant === 'overlay' ? 'border-t border-border-subtle px-[22px] py-4' : ''}>
          <MessageComposer connectionRef={connectionRef} variant={variant} />
        </div>
      ) : (
        <p
          role="status"
          className="border-t border-border-subtle bg-card px-[22px] py-4 text-xs leading-snug text-muted-foreground"
        >
          {closed}
        </p>
      )}
    </div>
  );
}

/** §2.16's header: the avatar, the name, and *"Met at {event}"* — `MEM-CON-013`'s three. */
function ThreadHeader({ thread }: { readonly thread: Thread }): React.ReactElement {
  const line = roleAtCompany(thread);
  return (
    <header className="flex items-center gap-3.5 border-b border-border-subtle px-[22px] py-4">
      <span
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-sm font-extrabold text-brand-700"
        aria-hidden
      >
        {initialsOf(thread)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-brand text-md font-bold text-foreground">
          {personName(thread)}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {line === null ? threadSubtitle(thread) : `${line} · ${threadSubtitle(thread)}`}
        </p>
      </div>
    </header>
  );
}
